import type { MouseEvent, ReactNode } from 'react';

import { DropdownButton } from '@/ux-common/components/dropdown-button/dropdown-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/ux-common/components/popover/popover';

import { useCompressorAlarm } from './hooks/use-compressor-alarm';

interface CompressorAlarmCodeProps {
  className?: string;
  deviceId: string;
}

// The control sits on the card, which opens the device's details on click. React
// bubbles events from the portalled popover through the card too.
const stopPropagation = (event: MouseEvent) => event.stopPropagation();

/**
 * The compressor micro-panel's current alarm: an Alarm Code dropdown button that opens
 * a popover with the alarm message and its code. Renders nothing without an alarm.
 * The popover uses the registry DropdownMenu panel, as the registry's FilterTrigger does.
 */
export const CompressorAlarmCode = ({
  className,
  deviceId,
}: CompressorAlarmCodeProps): ReactNode => {
  const alarm = useCompressorAlarm(deviceId);
  if (!alarm) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <DropdownButton
          aria-haspopup="dialog"
          className={className}
          data-component="CompressorAlarmCode"
          data-testid="compressor-alarm-code"
          onClick={stopPropagation}
        >
          Alarm Code
        </DropdownButton>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="dropdown-menu"
        data-testid="compressor-alarm-code-popover"
        onClick={stopPropagation}
      >
        <div className="dropdown-menu-item justify-between">
          <span>{alarm.message}</span>
          <span className="shrink-0 text-[color:var(--dropdown-menu-label-fg)]">
            Code {alarm.code}
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
};
